import React, { useState } from "react";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import { useQuery, useMutation, gql } from "@apollo/client";
import Loader from "../../Loader";

const GET_LEADS = gql`
  query GetLeads($data: GetLeadsFilter) {
    getLeads(data: $data) {
      status
      message
      leads {
        id
        source
        leadgenId
        formId
        adId
        fullName
        phoneNumber
        email
        status
        details
        leadCreatedAt
        createdAt
      }
    }
  }
`;

// Where a lead came from, in words the team uses.
const SOURCE_LABELS = {
  META: "Meta ad · form",
  META_CTWA: "Meta ad · WhatsApp",
  WHATSAPP_ORGANIC: "WhatsApp · direct",
  APP_BRIDAL: "App · bridal consultation",
};

const UPDATE_LEAD_STATUS = gql`
  mutation UpdateLeadStatus($leadId: ID!, $status: String!) {
    updateLeadStatus(leadId: $leadId, status: $status) {
      status
      message
    }
  }
`;

const STATUS_BADGE = {
  NEW: "bg-blue-100 text-blue-800",
  CONTACTED: "bg-yellow-100 text-yellow-800",
  CONVERTED: "bg-green-100 text-green-800",
  INVALID: "bg-gray-100 text-gray-500",
  DUPLICATE: "bg-gray-100 text-gray-500",
};

const STATUS_OPTIONS = ["NEW", "CONTACTED", "CONVERTED", "INVALID", "DUPLICATE"];

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const LeadManagement = () => {
  const [toast, setToast] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const filter = {};
  if (filterStatus) filter.status = filterStatus;
  if (filterSource) filter.source = filterSource;

  const { data, loading, refetch } = useQuery(GET_LEADS, {
    variables: { data: filter },
    fetchPolicy: "network-only",
  });

  const [updateLeadStatus] = useMutation(UPDATE_LEAD_STATUS);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleStatusChange = async (lead, newStatus) => {
    if (newStatus === lead.status) return;
    setUpdatingId(lead.id);
    try {
      const res = await updateLeadStatus({ variables: { leadId: lead.id, status: newStatus } });
      if (res.data?.updateLeadStatus?.status === "SUCCESS") {
        showToast(`Lead marked as ${newStatus}`);
        refetch();
      } else {
        showToast(res.data?.updateLeadStatus?.message || "Failed to update lead", "error");
      }
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setUpdatingId(null);
    }
  };

  const leads = data?.getLeads?.leads || [];

  const stats = {
    total: leads.length,
    new: leads.filter((l) => l.status === "NEW").length,
    contacted: leads.filter((l) => l.status === "CONTACTED").length,
    converted: leads.filter((l) => l.status === "CONVERTED").length,
  };

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="p-6 bg-white w-full rounded-lg shadow-md m-4 overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Leads</h1>
              <p className="text-sm text-gray-500 mt-1">
                Leads from Meta ads (forms and WhatsApp), WhatsApp chats and the app.
              </p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { label: "Total", value: stats.total, color: "blue" },
              { label: "New", value: stats.new, color: "blue" },
              { label: "Contacted", value: stats.contacted, color: "yellow" },
              { label: "Converted", value: stats.converted, color: "green" },
            ].map((stat) => (
              <div
                key={stat.label}
                className={`bg-${stat.color}-50 border border-${stat.color}-100 p-4 rounded-lg text-center`}
              >
                <div className={`text-sm text-${stat.color}-600 font-semibold mb-1`}>{stat.label}</div>
                <div className={`text-3xl font-bold text-${stat.color}-800`}>{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-5 bg-gray-50 p-4 rounded-lg border">
            <div className="flex-1 max-w-xs">
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                className="w-full border p-2 rounded bg-white text-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 max-w-xs">
              <label className="block text-xs font-medium text-gray-600 mb-1">Source</label>
              <select
                className="w-full border p-2 rounded bg-white text-sm"
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
              >
                <option value="">All Sources</option>
                {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilterStatus("");
                  setFilterSource("");
                }}
                className="px-4 py-2 border rounded bg-white text-sm hover:bg-gray-100 transition"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Leads Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="py-3 px-4 rounded-tl-lg">Name</th>
                  <th className="py-3 px-4">Phone</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4">Source</th>
                  <th className="py-3 px-4">Received</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 rounded-tr-lg">Update Status</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-10 text-gray-400">
                      No leads found.
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead.id} className="border-b hover:bg-gray-50 transition">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-800">{lead.fullName || "—"}</div>
                        {lead.details ? (
                          <div className="text-xs text-gray-600 max-w-xs">{lead.details}</div>
                        ) : (
                          <div className="text-xs text-gray-400">{lead.leadgenId}</div>
                        )}
                      </td>
                      <td className="py-3 px-4">{lead.phoneNumber || "—"}</td>
                      <td className="py-3 px-4">{lead.email || "—"}</td>
                      <td className="py-3 px-4">
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          {SOURCE_LABELS[lead.source] || lead.source || "—"}
                        </span>
                        {lead.adId && <div className="text-xs text-gray-400 mt-1">Ad {lead.adId}</div>}
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-600">
                        {formatDate(lead.leadCreatedAt || lead.createdAt)}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            STATUS_BADGE[lead.status] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {lead.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <select
                          className="text-xs border p-1.5 rounded bg-white disabled:opacity-50"
                          value={lead.status}
                          disabled={updatingId === lead.id}
                          onChange={(e) => handleStatusChange(lead, e.target.value)}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition ${
            toast.type === "error" ? "bg-red-600" : "bg-green-600"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default LeadManagement;
